import React, { useState, useRef } from 'react';
import { Upload, Download, Type, Layers, Image as ImageIcon, RefreshCcw, Check } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';

const OVERLAYS = [
  { id: 'neon', name: 'Neon Cyber', src: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=600', style: 'border-2 border-brand-neonBlue' },
  { id: 'minimal', name: 'Corporate Clean', src: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=600', style: 'bg-white/10' },
  { id: 'floral', name: 'Wedding Floral', src: 'https://images.unsplash.com/photo-1519225421980-715cb0202128?auto=format&fit=crop&q=80&w=600', style: 'border-2 border-brand-neonPink' },
];

export const MockupStudio = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const [logo, setLogo] = useState<string | null>(null);
  const [text, setText] = useState('EVENT 2025');
  const [selectedOverlay, setSelectedOverlay] = useState(OVERLAYS[0]);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const url = URL.createObjectURL(e.target.files[0]);
      setLogo(url);
    }
  };

  const downloadMockup = () => {
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
      alert("In a real app, this would download the composited image!");
    }, 1500);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative">
      {/* Background */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-neonBlue/5 to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Controls */}
        <div className="lg:col-span-4 space-y-6 animate-fade-in-up">
          <div>
            <h1 className="text-3xl font-display font-bold text-white mb-2">Mockup Studio</h1>
            <p className="text-slate-400 text-sm">Visualize your brand on our booth output. Perfect for pitch decks.</p>
          </div>

          <GlassCard className="space-y-6">
            
            {/* 1. Upload Logo */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-white mb-3">
                <Upload size={16} className="text-brand-neonBlue" /> 1. Upload Logo
              </label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-700 rounded-xl p-8 text-center hover:border-brand-neonBlue hover:bg-slate-800/50 transition-all cursor-pointer group"
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  accept="image/png, image/jpeg, image/svg+xml" 
                  onChange={handleLogoUpload}
                />
                {logo ? (
                  <div className="flex flex-col items-center">
                    <img src={logo} alt="Uploaded" className="h-12 w-auto object-contain mb-2" />
                    <span className="text-xs text-green-400 flex items-center gap-1"><Check size={10} /> Logo Loaded</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-slate-500 group-hover:text-slate-300">
                    <ImageIcon size={32} className="mb-2" />
                    <span className="text-xs">Click to browse (PNG/Transp)</span>
                  </div>
                )}
              </div>
            </div>

            {/* 2. Choose Style */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-white mb-3">
                <Layers size={16} className="text-brand-neonPink" /> 2. Select Vibe
              </label>
              <div className="grid grid-cols-3 gap-2">
                {OVERLAYS.map((overlay) => (
                  <button
                    key={overlay.id}
                    onClick={() => setSelectedOverlay(overlay)}
                    className={`relative rounded-lg overflow-hidden aspect-square border-2 transition-all ${
                      selectedOverlay.id === overlay.id ? 'border-brand-neonBlue scale-105 shadow-[0_0_15px_rgba(34,211,238,0.3)]' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={overlay.src} alt={overlay.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/80 to-transparent p-1">
                      <span className="text-[10px] font-bold text-white truncate">{overlay.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Edit Text */}
            <div>
              <label className="flex items-center gap-2 text-sm font-bold text-white mb-3">
                <Type size={16} className="text-yellow-400" /> 3. Event Hashtag/Date
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-brand-neonBlue outline-none"
              />
            </div>

            <Button 
              className="w-full mt-4" 
              onClick={downloadMockup} 
              disabled={isDownloading}
            >
              {isDownloading ? 'Processing...' : 'Download Mockup'}
              {!isDownloading && <Download size={18} className="ml-2" />}
            </Button>
            
            <button 
               onClick={() => { setLogo(null); setText('EVENT 2025'); }}
               className="w-full text-center text-xs text-slate-500 hover:text-white flex items-center justify-center gap-1 mt-2"
            >
              <RefreshCcw size={10} /> Reset Canvas
            </button>

          </GlassCard>
        </div>

        {/* Right Preview */}
        <div className="lg:col-span-8 flex items-center justify-center bg-slate-900/50 rounded-3xl border border-white/5 relative overflow-hidden min-h-[600px] animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
           
           {/* Phone Frame */}
           <div className="relative w-[300px] h-[600px] bg-black rounded-[3rem] border-8 border-slate-800 shadow-2xl overflow-hidden z-10">
              {/* Dynamic Content */}
              <div className="absolute inset-0">
                {/* Background Image (The "Photo") */}
                <img 
                  src="https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&q=80&w=800" 
                  className="w-full h-full object-cover" 
                  alt="Party People"
                />
                
                {/* Overlay Layer */}
                <div className={`absolute inset-0 pointer-events-none mix-blend-screen opacity-60 ${
                  selectedOverlay.id === 'neon' ? 'bg-gradient-to-tr from-brand-neonBlue/40 to-brand-neonPink/40' : ''
                }`}></div>

                {/* Branding Layer (Composited) */}
                <div className="absolute inset-0 flex flex-col justify-between p-6 z-20 pointer-events-none">
                  
                  {/* Top Logo Area */}
                  <div className="h-20 w-full flex items-center justify-center">
                    {logo ? (
                      <img src={logo} alt="Brand Logo" className="max-h-full max-w-[80%] object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" />
                    ) : (
                      <div className="border-2 border-dashed border-white/30 rounded px-4 py-2 text-white/50 text-xs font-bold uppercase">
                        Your Logo Here
                      </div>
                    )}
                  </div>

                  {/* Animated Frame Elements based on selection */}
                  {selectedOverlay.id === 'neon' && (
                    <div className="absolute inset-4 border-2 border-brand-neonBlue rounded-xl shadow-[0_0_20px_rgba(34,211,238,0.5)]"></div>
                  )}
                  {selectedOverlay.id === 'floral' && (
                    <div className="absolute inset-0 border-[10px] border-white/20"></div>
                  )}

                  {/* Bottom Text Area */}
                  <div className="text-center">
                    <h3 className={`text-2xl font-bold text-white drop-shadow-md ${
                      selectedOverlay.id === 'neon' ? 'font-display tracking-widest' : 'font-serif italic'
                    }`}>
                      {text}
                    </h3>
                  </div>
                </div>
              </div>

              {/* iPhone Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-xl z-30"></div>
           </div>

           {/* Floating Tags */}
           <div className="absolute top-1/2 left-10 hidden md:block">
              <div className="glass-panel px-4 py-2 rounded-lg text-xs font-mono text-brand-neonBlue mb-2">
                &lt;Overlay_Layer&gt;
              </div>
              <div className="w-16 h-[1px] bg-brand-neonBlue/50"></div>
           </div>
           <div className="absolute bottom-20 right-10 hidden md:block text-right">
              <div className="glass-panel px-4 py-2 rounded-lg text-xs font-mono text-brand-neonPink mb-2">
                Brand_Integrity: 100%
              </div>
              <div className="w-16 h-[1px] bg-brand-neonPink/50 ml-auto"></div>
           </div>

        </div>
      </div>
      
      {/* CTA Band */}
      <div className="max-w-3xl mx-auto mt-16 text-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <h2 className="text-2xl font-bold text-white mb-4">Happy with the look?</h2>
        <div className="flex justify-center gap-4">
          <Button onClick={onOpenQuote}>Get a Quote for this Setup</Button>
          <Button variant="outline" onClick={() => window.print()}>Save PDF Spec</Button>
        </div>
      </div>

    </div>
  );
};